import type {
  SearchCategory,
  SelectableItem,
  SelectableSearchCategory
} from '~/features/catalog/components/navigation/CategoryNavigation';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState } from 'react';
import { cn } from '~/common/utils/styleUtil';
import { HwgIcon } from '~/features/catalog/components/navigation/HwgIcon';
import { LucideChevronRight } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '~/common/components/ui/hover-card';
import { CircularProgress } from '~/common/components/CircularProgress';
import { useIsNavigatingToCategory } from '~/features/catalog/hooks/useIsNavigatingToCategory';
import { useSpinDelay } from 'spin-delay';
import { TrackNavigationClick } from '~/tracking/TrackNavigationClick';
import { isNavigationLeaf } from '~/features/catalog/utils/categoryNavigationUtil';

type ChildCategoryItemProps = {
  onCategorySelected: (category: SelectableSearchCategory) => void;
  onShowAllInCategorySelected: (category: SelectableSearchCategory) => void;
  selectableCategory: SelectableItem<SearchCategory>;
  isSelectedBranch: boolean;
  compact: boolean;
  hasShowAllItem?: boolean;
  indent: boolean;
};

export function ChildCatalogNavigationItem({
  onCategorySelected,
  onShowAllInCategorySelected,
  selectableCategory,
  isSelectedBranch,
  compact,
  hasShowAllItem,
  indent
}: Readonly<ChildCategoryItemProps>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { isNavigatingToCategory } = useIsNavigatingToCategory({ selectableCategory });
  const showProgress = useSpinDelay(isNavigatingToCategory, { delay: 200, minDuration: 600 });

  const handleClick = useCallback(
    (item: SelectableItem<SearchCategory>) => {
      onCategorySelected(item);
      setOpen(false);
    },
    [onCategorySelected]
  );

  // Initialer Baum: kein Knoten selektiert -> kein Popper und kein Icon anzeigen.
  // Desktop: Wenn Knoten selektiert, dann Popper und Icon nur bei den übergeordneten Knoten anzeigen
  // Mobile: Wenn Knoten selektiert, Back Icon auf Gesamtsortiment Icon nur bei den übergeordneten Knoten anzeigen

  const navigationItem = useMemo(() => {
    const shouldTrackCategoryClick = !compact || isNavigationLeaf(selectableCategory);
    const categoryButton = (
      <button type="button"
        className={cn(
          'pl-0 w-full text-left hover:text-primary flex gap-2 items-center cursor-pointer',
          indent && 'pl-4',
          selectableCategory.selected && selectableCategory.item.type !== null && 'font-bold',
          open && 'bg-neutral-100'
        )}
        data-testid={`catalog-navigation-${selectableCategory.item.name}`}
        onClick={() => onCategorySelected(selectableCategory)}
      >
        <HwgIcon category={selectableCategory} />
        <div className={'flex-1 p-3 pl-0'}>{selectableCategory.item.name}</div>
      </button>
    );

    return (
      <div className={cn('flex border-b items-center', isSelectedBranch && 'hover:bg-neutral-100')}>
        {shouldTrackCategoryClick ? (
          <TrackNavigationClick label={selectableCategory.item.name}>{categoryButton}</TrackNavigationClick>
        ) : (
          categoryButton
        )}
        {!compact && !showProgress && isSelectedBranch && (
          <HoverCard open={open} onOpenChange={setOpen} openDelay={0} closeDelay={0}>
            <HoverCardTrigger>
              <div className={'py-4 pr-1'}>
                <LucideChevronRight size={16} />
              </div>
            </HoverCardTrigger>
            <HoverCardContent
              asChild
              side={'right'}
              align={'start'}
              sideOffset={0}
              className={
                'p-0 max-h-[calc(100vh-120px)] font-normal w-[300px] overflow-y-auto bg-neutral-100 rounded-l-none border-none shadow-none'
              }
            >
              <div>
                {selectableCategory.children.map(x => (
                  <TrackNavigationClick key={x.item.id + x.item.name} label={x.item.name}>
                    <button type="button"
                      className={cn('w-full text-start p-3 border-b hover:text-primary')}
                      onClick={() => handleClick(x)}
                    >
                      {x.item.name}
                    </button>
                  </TrackNavigationClick>
                ))}
              </div>
            </HoverCardContent>
          </HoverCard>
        )}
        {showProgress && <CircularProgress size={'sm'} />}
      </div>
    );
  }, [compact, handleClick, indent, isSelectedBranch, onCategorySelected, open, selectableCategory, showProgress]);

  return (
    <>
      {navigationItem}
      {hasShowAllItem && (
        <TrackNavigationClick label={`${t('Alle anzeigen')}: ${selectableCategory.item.name}`}>
          <button
            type="button"
            className={'text-left p-3 pl-4 border-b w-full'}
            onClick={() => onShowAllInCategorySelected(selectableCategory)}
          >
            {t('Alle anzeigen')}
          </button>
        </TrackNavigationClick>
      )}
    </>
  );
}
